import React, { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { getSuppliers, createSupplier, updateSupplier, deleteSupplier } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Pencil, Tractor, Package } from "lucide-react";
import { useT } from "@/i18n/I18nContext";

const empty = {
  id: null, name: "", contact_person: "", phone: "", email: "",
  address: "", tax_id: "", notes: "",
};

export default function Suppliers({ category = "milk" }) {
  const { t } = useT();
  const { can } = useAuth();
  const canManage = can("admin", "manager");
  const [suppliers, setSuppliers] = useState([]);
  const [dialog, setDialog] = useState(null);
  const Icon = category === "other" ? Package : Tractor;
  const titleKey = category === "other" ? "suppliers.titleOther" : "suppliers.titleMilk";

  const load = async () => setSuppliers(await getSuppliers({ category }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { (async () => { await load(); })(); }, [category]);

  const save = async (f) => {
    try {
      if (!f.name?.trim()) { toast.error(t("suppliers.nameRequired")); return; }
      const payload = {
        name: f.name, category, contact_person: f.contact_person, phone: f.phone,
        email: f.email, address: f.address, tax_id: f.tax_id, notes: f.notes,
      };
      if (f.id) await updateSupplier(f.id, payload);
      else await createSupplier(payload);
      toast.success(t("suppliers.saved"));
      setDialog(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
  };

  const remove = async (id) => {
    try { await deleteSupplier(id); toast.success(t("suppliers.removed")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
  };

  return (
    <Layout actions={
      canManage ? (
        <Button size="sm" onClick={() => setDialog({ ...empty })} data-testid="add-supplier-button">
          <Plus className="mr-1.5 h-4 w-4" /> {t("suppliers.add")}
        </Button>
      ) : null
    }>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Icon className="h-4 w-4 text-[hsl(var(--brand))]" /> {t(titleKey)} ({suppliers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {suppliers.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center" data-testid="suppliers-empty">
              <Icon className="h-10 w-10 text-muted-foreground/50" />
              <p className="mt-3 text-sm text-muted-foreground">{t("suppliers.empty")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto thin-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("common.name")}</TableHead>
                    <TableHead>{t("suppliers.contact")}</TableHead>
                    <TableHead>{t("common.phone")}</TableHead>
                    <TableHead>{t("common.email")}</TableHead>
                    <TableHead>{t("suppliers.taxId")}</TableHead>
                    {canManage && <TableHead></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {suppliers.map((s) => (
                    <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell className="text-muted-foreground">{s.contact_person || "-"}</TableCell>
                      <TableCell className="font-mono text-xs">{s.phone || "-"}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">{s.email || "-"}</TableCell>
                      <TableCell className="font-mono text-xs">{s.tax_id || "-"}</TableCell>
                      {canManage && (
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => setDialog({ ...empty, ...s })} data-testid={`supplier-edit-${s.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => remove(s.id)} data-testid={`supplier-delete-${s.id}`}>
                            <Trash2 className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <SupplierDialog key={dialog ? (dialog.id ?? "new") : "closed"} dialog={dialog} onClose={() => setDialog(null)} onSave={save} />
    </Layout>
  );
}

function SupplierDialog({ dialog, onClose, onSave }) {
  const { t } = useT();
  const [f, setF] = useState(dialog);
  if (!f) return null;
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  return (
    <Dialog open={!!dialog} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{f.id ? t("suppliers.editSupplier") : t("suppliers.addSupplier")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto thin-scroll pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5 col-span-2">
              <Label>{t("suppliers.supplierName")}</Label>
              <Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="supplier-name-input" placeholder={t("milk.farmName")} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("suppliers.contactPerson")}</Label>
              <Input value={f.contact_person} onChange={(e) => set("contact_person", e.target.value)} data-testid="supplier-contact-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("common.phone")}</Label>
              <Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="supplier-phone-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("common.email")}</Label>
              <Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="supplier-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("suppliers.taxId")}</Label>
              <Input value={f.tax_id} onChange={(e) => set("tax_id", e.target.value)} data-testid="supplier-taxid-input" />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>{t("common.address")}</Label>
              <Input value={f.address} onChange={(e) => set("address", e.target.value)} data-testid="supplier-address-input" />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>{t("common.notes")}</Label>
              <Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="supplier-notes-input" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={() => onSave(f)} data-testid="supplier-save-button">{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
